import React from 'react';

import { Textarea } from '@/components/ui/catalyst/textarea';

import type { NotesEditorProps } from '../types';

/**
 * Reusable notes editor component for workflow configuration.
 *
 * Used by both firm and matter workflow rows for consistent
 * editing experience across contexts.
 *
 * The edit trigger is now the inline button in WorkflowRowBase.
 * This component handles: editing mode UI + read-only display of notes.
 */
export const NotesEditor: React.FC<NotesEditorProps> = ({
  workflowCode,
  notes,
  notesValue,
  isEditing,
  isSaving,
  disabled,
  isWorkflowDisabled,
  placeholder = 'Add notes...',
  displayLabel = 'Guideline',
  onNotesValueChange,
  onSave,
  onCancel,
}) => {
  // Hide entirely if not editing and no notes to show
  if (!isEditing && !notes) {
    return null;
  }

  return (
    <div className={`mt-0.5 ${isWorkflowDisabled ? 'opacity-50' : ''}`}>
      {isEditing ? (
        <div className="space-y-1">
          <Textarea
            id={`notes-${workflowCode}`}
            value={notesValue}
            onChange={(e) => onNotesValueChange(e.target.value)}
            placeholder={placeholder}
            rows={1}
            disabled={disabled || isSaving}
            className="text-[11px] py-1.5 px-2 min-h-0"
          />
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onSave}
              disabled={disabled || isSaving}
              className="px-1.5 py-0.5 text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="px-1.5 py-0.5 text-[11px] font-medium text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        // Read-only display of existing notes (edit trigger is now the inline button)
        <p className="text-[11px] text-zinc-600 dark:text-zinc-400">
          <span className="font-medium text-zinc-700 dark:text-zinc-300">
            {displayLabel}:
          </span>{' '}
          {notes}
        </p>
      )}
    </div>
  );
};
